/**
 * Build-time BODY prerender for the Vue-SPA static-HTML emitter.
 *
 * The companion to `emitStaticSeoHtml` (which bakes per-route `<head>` meta +
 * JSON-LD). Today every SPA route's emitted `<body>` is literally
 * `<div id="app"></div>` — allow-listed AI crawlers see the `@graph` + llms.txt
 * but ZERO body prose (blog copy, service descriptions, GEO-citable content).
 * This module fills the mount container with the REAL rendered DOM so a non-JS
 * crawler sees the page's body content.
 *
 * ── Why a render pass, not a content.yaml dump ────────────────────────────────
 * `.dcs/content.yaml` stores only visual-editor TEXT OVERRIDES — "Vue component
 * `defaults` objects are the source of truth for baseline text" (its own header).
 * Blog-post prose lives entirely in the site's `src/data/*.ts` and renders via
 * `v-html`; it is NOT in content.yaml at all. So the only faithful, generic way
 * to obtain a route's body is to RENDER the built app. This module is therefore
 * renderer-agnostic: it takes a {@link RouteRenderer} (the production default is
 * a headless-Playwright renderer of the just-built `dist/`; tests inject a fake)
 * and is otherwise pure — deterministic, `fs`-only, and unit-testable.
 *
 * ── Fabrication safety (the A-34 scar) ────────────────────────────────────────
 * A prior cms SSR path baked fabricated review defaults into crawler-visible
 * HTML. The default renderer drives the PRODUCTION client bundle, where Vite has
 * statically replaced `import.meta.env.DEV` with `false`, so the dev-only
 * `defaults` honesty gate (`useReviewContent.ts`) resolves to the honest empty
 * state — fabrication is structurally impossible, not merely avoided. Any route
 * that instead CRASHES (uncaught exception, or the app never mounts) surfaces as
 * a {@link BodyPrerenderError} and FAILS THE BUILD LOUD rather than shipping a
 * broken/empty body.
 */

import fs from 'node:fs'
import path from 'node:path'
import type { PageRouteEntry } from './pagesManifest'
import { matchesExcludedGlob } from './sitemap'
import { extractBakedHead, type HeadObservation } from './headHonesty'

/**
 * Normalise a route path to the on-disk output file the head emitter wrote:
 *   '/'               -> '<outDir>/index.html'
 *   '/services'       -> '<outDir>/services/index.html'
 *   '/blog/my-post'   -> '<outDir>/blog/my-post/index.html'
 *
 * Lives here (the lower-level module) so both this pass and `dcsSeoPlugin`'s
 * head emitter import it without a plugin↔module cycle.
 */
export function routeToOutputFile(outDir: string, routePath: string): string {
  const trimmed = routePath.replace(/^\/+/, '').replace(/\/+$/, '')
  if (trimmed === '') return path.join(outDir, 'index.html')
  return path.join(outDir, ...trimmed.split('/'), 'index.html')
}

/**
 * The `<head>` values the app left behind after it mounted — i.e. what a
 * JS-rendering crawler (and every human) actually sees. Captured in the SAME
 * render pass that produces the body, so the P1 head-honesty rail costs no extra
 * page loads. See `headHonesty.ts` for what is done with it.
 */
export interface RenderedHead {
  /** `document.title` after mount. */
  title: string | null
  /** The EFFECTIVE `meta[name=description]` (the last one in the DOM). */
  description: string | null
  /** How many `meta[name=description]` tags exist — >1 is itself a smell. */
  descriptionCount: number
}

/** The rendered body of one route plus any fatal render errors observed. */
export interface RenderedRoute {
  /** The `innerHTML` of the app mount container (`#app`) after the app rendered. */
  bodyHtml: string
  /**
   * The post-mount `<head>` observations, when the renderer captures them. The
   * fake renderers in unit tests may omit it; the production Playwright renderer
   * always provides it.
   */
  head?: RenderedHead
  /**
   * FATAL render errors (uncaught exceptions / failed mount) that mean the body
   * is untrustworthy. A non-empty list makes `prerenderBodies` throw a
   * {@link BodyPrerenderError} so the build fails loud instead of shipping wrong
   * content. Benign offline noise (blocked external asset, expected API 401) is
   * NOT reported here by the default renderer.
   */
  errors: string[]
}

/**
 * A build-time renderer that returns the rendered body for a route path. The
 * production implementation ({@link createPlaywrightRenderer}) drives the built
 * SPA in a headless browser; unit tests pass a deterministic fake.
 */
export interface RouteRenderer {
  renderRoute(routePath: string): Promise<RenderedRoute>
}

/**
 * Thrown when a route's render produced FATAL errors (a crash or a mount
 * failure). Deliberately escapes the plugin's never-break-the-build try/catch so
 * a component that would crash or emit wrong content FAILS THE BUILD (hard
 * requirement: never ship fabricated/broken body content).
 */
export class BodyPrerenderError extends Error {
  readonly route: string
  readonly errors: string[]
  constructor(route: string, errors: string[]) {
    super(
      `[dcs-seo] body prerender FAILED for route ${route}: ${errors.join(' | ')}. ` +
        `Refusing to ship a broken/empty body — fix the route or disable body ` +
        `prerender for this site (seo.yaml prerenderBody: false).`
    )
    this.name = 'BodyPrerenderError'
    this.route = route
    this.errors = errors
  }
}

/**
 * Replace the inner content of the FIRST `<div id="app">…</div>` mount container
 * with `bodyInnerHtml`, leaving `<head>`, the app `<script>`, and everything
 * else untouched. Free of a DOM dependency (mirrors `spliceHeadHtml`), and
 * robust to nested `<div>`s via depth counting so it also works when re-run over
 * an already-filled container (idempotent given a deterministic renderer).
 *
 * Returns the HTML unchanged when there is no `#app` container (defensive — the
 * caller logs + skips), so a non-SPA shell can never be corrupted.
 */
export function spliceBodyHtml(html: string, bodyInnerHtml: string): string {
  const openRe = /<div\s+[^>]*\bid=["']app["'][^>]*>/i
  const openMatch = openRe.exec(html)
  if (!openMatch) return html

  const openEnd = openMatch.index + openMatch[0].length

  // Find the matching </div> for the opening #app tag via depth counting over
  // every subsequent <div…> / </div>. Void/self-closing divs don't exist, so a
  // simple open/close depth walk finds the true container close even with nested
  // markup (the rendered app tree).
  const tagRe = /<(\/?)div\b[^>]*>/gi
  tagRe.lastIndex = openEnd
  let depth = 1
  let closeStart = -1
  let m: RegExpExecArray | null
  while ((m = tagRe.exec(html)) !== null) {
    if (m[1] === '/') {
      depth -= 1
      if (depth === 0) {
        closeStart = m.index
        break
      }
    } else {
      depth += 1
    }
  }
  // Malformed (no matching close) — leave the document alone rather than risk a
  // truncated write.
  if (closeStart === -1) return html

  return html.slice(0, openEnd) + bodyInnerHtml + html.slice(closeStart)
}

/** Inputs for {@link prerenderBodies}. */
export interface PrerenderBodiesParams {
  /** Absolute path to the built SPA output directory (`dist`). */
  outDir: string
  /** The SAME route set the head emitter used (from `.dcs/pages.yaml`). */
  routes: PageRouteEntry[]
  /** The renderer that returns each route's rendered body. */
  renderer: RouteRenderer
  /** Routes to skip entirely — matched against route `path` OR `slug`. */
  exclude?: string[]
  /**
   * Routes that received `robots: noindex,nofollow` (auth-gated / private). These
   * are NOT body-prerendered — a noindex/auth route must never leak rendered body
   * content into `dist/` (hard requirement). Matched against `path` OR `slug`.
   */
  noindex?: string[]
  /** `pages.yaml` top-level `excluded:` globs — matching routes are skipped. */
  excludedGlobs?: string[]
  /**
   * Write the rendered body back into the per-route file (default `true`).
   *
   * Set `false` to run the pass purely as an OBSERVER: routes are still rendered
   * and their `<head>` still captured for the P1 honesty rail, but `dist/` is not
   * touched. That is what makes head honesty checkable on a site that has opted
   * out of body prerender (`seo.yaml prerenderBody: false`) without giving it a
   * second, duplicate browser pass.
   */
  spliceBody?: boolean
  debug?: boolean
}

/** Why a route never reached the renderer. */
export type PrerenderFilterReason =
  | 'excluded'
  | 'excluded-glob'
  | 'noindex'
  | 'no-head-file'

/** One route the pass never rendered, and why. */
export interface FilteredRoute {
  route: string
  reason: PrerenderFilterReason
}

/** Outcome of a body-prerender pass. */
export interface PrerenderBodiesResult {
  /** Number of per-route files whose body was filled. */
  prerendered: number
  /** Number of routes skipped (excluded / noindex / missing head file / empty). */
  skipped: number
  /** The route paths that were prerendered, in order (for logging/tests). */
  routes: string[]
  /**
   * Per-route baked-vs-rendered `<head>` observations, collected for every route
   * this pass rendered (including ones whose body was skipped as empty). Consumed
   * by the P1 rail; empty when the renderer does not report head values.
   */
  headObservations: HeadObservation[]
  /**
   * Every route that actually reached `renderer.renderRoute` — i.e. the set the
   * P1 head-honesty rail COULD have observed. This is the denominator that makes
   * "the rail ran" checkable: `headObservations.length < eligibleRoutes.length`
   * means some eligible route produced no observation, and an empty
   * `eligibleRoutes` means the pass checked nothing at all (C-338).
   */
  eligibleRoutes: string[]
  /**
   * Routes that never reached the renderer, each with the reason. Lets the
   * caller name exactly what was NOT checked instead of reporting a bare zero.
   */
  filteredRoutes: FilteredRoute[]
}

/**
 * For each emitted route: render its body and splice it into the per-route
 * `dist/<path>/index.html` the head emitter already wrote. Reuses the exact same
 * exclude/excluded-glob filters as `emitStaticSeoHtml`, and ADDITIONALLY skips
 * every `noindex` route (auth-gated/private pages get a head-only file, never a
 * prerendered body).
 *
 * FAILS LOUD (throws {@link BodyPrerenderError}) when a route's render reports
 * fatal errors — better a red build than fabricated/broken crawler content.
 * Pure aside from `fs` reads/writes; the browser lives behind `renderer`.
 */
export async function prerenderBodies(
  params: PrerenderBodiesParams
): Promise<PrerenderBodiesResult> {
  const {
    outDir,
    routes,
    renderer,
    exclude = [],
    noindex = [],
    excludedGlobs = [],
    spliceBody = true,
    debug = false,
  } = params

  const excludeSet = new Set(exclude)
  const noindexSet = new Set(noindex)

  let prerendered = 0
  let skipped = 0
  const done: string[] = []
  const headObservations: HeadObservation[] = []
  const eligibleRoutes: string[] = []
  const filteredRoutes: FilteredRoute[] = []

  for (const route of routes) {
    // (1) Same exclusions as the head emitter.
    if (excludeSet.has(route.path) || (route.slug && excludeSet.has(route.slug))) {
      skipped += 1
      filteredRoutes.push({ route: route.path, reason: 'excluded' })
      if (debug) console.log(`[dcs-seo] body-prerender skip (excluded): ${route.path}`)
      continue
    }
    if (matchesExcludedGlob(route.path, excludedGlobs)) {
      skipped += 1
      filteredRoutes.push({ route: route.path, reason: 'excluded-glob' })
      if (debug) console.log(`[dcs-seo] body-prerender skip (excluded glob): ${route.path}`)
      continue
    }
    // (2) HARD REQUIREMENT: never prerender a noindex / auth-gated route's body.
    if (noindexSet.has(route.path) || (route.slug && noindexSet.has(route.slug))) {
      skipped += 1
      filteredRoutes.push({ route: route.path, reason: 'noindex' })
      if (debug) console.log(`[dcs-seo] body-prerender skip (noindex/auth): ${route.path}`)
      continue
    }

    const outFile = routeToOutputFile(outDir, route.path)
    if (!fs.existsSync(outFile)) {
      // The head emitter should have written this; if not, there's nothing to
      // fill. Skip rather than fabricate a file.
      skipped += 1
      filteredRoutes.push({ route: route.path, reason: 'no-head-file' })
      if (debug) console.log(`[dcs-seo] body-prerender skip (no head file): ${route.path}`)
      continue
    }

    // From here the route reaches the renderer, so P1 is entitled to an
    // observation for it. Recorded BEFORE the render so a route that throws is
    // still counted as eligible-but-unobserved rather than vanishing.
    eligibleRoutes.push(route.path)
    const rendered = await renderer.renderRoute(route.path)

    // (3) FAIL LOUD on a crash / mount failure — never ship broken body content.
    if (rendered.errors.length > 0) {
      throw new BodyPrerenderError(route.path, rendered.errors)
    }

    // The file on disk still carries ONLY the baked <head> at this point (the
    // splice below touches the body), so this read is the honest baked side of
    // the P1 comparison regardless of where it happens in the loop.
    const html = fs.readFileSync(outFile, 'utf8')

    // (4) P1: record what the app left in the <head> vs what was baked. Captured
    // for every route we rendered — including ones whose body turns out empty,
    // because a route with no body can still overwrite its title.
    if (rendered.head) {
      const baked = extractBakedHead(html)
      headObservations.push({
        route: route.path,
        bakedTitle: baked.title,
        runtimeTitle: rendered.head.title,
        bakedDescription: baked.description,
        runtimeDescription: rendered.head.description,
        runtimeDescriptionCount: rendered.head.descriptionCount,
      })
    }

    const body = (rendered.bodyHtml ?? '').trim()
    if (body === '') {
      // A clean render that produced nothing (e.g. a genuinely empty route):
      // leave the shell as-is rather than emptying/altering it.
      skipped += 1
      if (debug) console.log(`[dcs-seo] body-prerender skip (empty render): ${route.path}`)
      continue
    }

    // Observer mode: the render happened (so head honesty is checkable) but the
    // site opted out of body prerender, so dist/ is left exactly as emitted.
    if (!spliceBody) {
      skipped += 1
      if (debug) console.log(`[dcs-seo] body-prerender skip (observe-only): ${route.path}`)
      continue
    }

    const spliced = spliceBodyHtml(html, body)
    if (spliced === html) {
      // No #app container found in this file — cannot splice safely.
      skipped += 1
      if (debug) console.log(`[dcs-seo] body-prerender skip (no #app container): ${route.path}`)
      continue
    }

    fs.writeFileSync(outFile, spliced, 'utf8')
    prerendered += 1
    done.push(route.path)
    if (debug) {
      console.log(
        `[dcs-seo] body-prerender wrote ${path.relative(outDir, outFile)} (${body.length} bytes)`
      )
    }
  }

  return { prerendered, skipped, routes: done, headObservations, eligibleRoutes, filteredRoutes }
}

/** Inputs for {@link isBodyPrerenderEnabled}. */
export interface BodyPrerenderEnableInput {
  /** Whether the per-route static-HTML emitter is on (body prerender rides it). */
  emitStaticHtml: boolean
  /** The plugin option `prerenderBody` (undefined ⇒ default ON when emitting). */
  prerenderBodyOption?: boolean
  /** Preview/staging build — body prerender is skipped (kept minimal + fast). */
  preview?: boolean
  /**
   * The per-site escape hatch read from `.dcs/seo.yaml` (`prerenderBody: false`).
   * Lets an operator disable body prerender for one site with NO code change and
   * NO cms republish — the portal owns seo.yaml. `undefined`/`true` ⇒ enabled.
   */
  seoPrerenderBody?: boolean
}

/**
 * Resolve whether body prerender runs for this build. ON by default whenever the
 * static-HTML emitter is on; turned OFF by the plugin option `prerenderBody:
 * false`, by the seo.yaml `prerenderBody: false` escape hatch, in preview mode,
 * or when the emitter itself is off. Either OFF signal wins.
 */
export function isBodyPrerenderEnabled(input: BodyPrerenderEnableInput): boolean {
  if (!input.emitStaticHtml) return false
  if (input.preview) return false
  if (input.prerenderBodyOption === false) return false
  if (input.seoPrerenderBody === false) return false
  return true
}
